import { ApiService, useAuthStore } from '@supply-mover-2-0/core';

import { VONAGE_STATE_KEY } from '../oauth-callback.constants';
import type {
	OAuthCallbackParams,
	OAuthCallbackResult,
	OAuthHandler,
} from '../oauth-callback.types';

export const VonageOAuthHandler: OAuthHandler = {
	name: 'vonage',

	canHandle(params: OAuthCallbackParams): boolean {
		const { code, state } = params;

		if (code == null || state == null) {
			return false;
		}

		// User flow uses 'user:' prefix on state
		if (state.startsWith('vonage-user:')) {
			return true;
		}

		// Admin flow checks against stored state
		const storedState = useAuthStore.getState().vonageOAuthState;

		return storedState === state;
	},

	async handle(params: OAuthCallbackParams): Promise<OAuthCallbackResult> {
		const { code, state } = params;

		if (code == null || state == null) {
			return {
				success: false,
				error: 'Missing authorization code or state',
			};
		}

		// Check if this is a user flow (state prefixed with 'user:')
		const isUserFlow = state.startsWith('vonage-user:');

		if (isUserFlow) {
			try {
				await ApiService.apiPost('/integrations/vonage/user-auth/callback', {
					code,
					state,
				});

				return {
					success: true,
					closePopup: true,
					postMessage: {
						type: 'vonage-user-oauth-callback',
						success: true,
						code,
						state,
					},
				};
			} catch (err) {
				console.error('Error handling user Vonage callback:', err);

				return {
					success: false,
					closePopup: true,
					postMessage: {
						type: 'vonage-user-oauth-callback',
						success: false,
						error: 'Failed to set up extension. Please try again.',
					},
				};
			}
		}

		// Admin flow
		try {
			await ApiService.apiPost('/integrations/vonage/auth/callback', {
				code,
				state,
			});

			useAuthStore.getState().setVonageOAuthState(null);

			return {
				success: true,
				redirectTo: '/settings/integrations',
			};
		} catch (err) {
			console.error('Error handling Vonage callback:', err);

			return {
				success: false,
				error: 'Failed to connect Vonage. Please try again.',
			};
		}
	},

	getStorageKey(): string {
		return VONAGE_STATE_KEY;
	},
};
